import React, { useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { MdOutlineDelete, MdEdit } from 'react-icons/md'
import { IoMdAdd, IoMdClose } from 'react-icons/io'
import Heading from '../../components/Heading'
import DeleteConfirm from '../../components/DeleteConfirm'
import axiosWrapper from '../../utils/AxiosWrapper'
import CustomButton from '../../components/CustomButton'
import NoData from '../../components/NoData'
import { CgDanger } from 'react-icons/cg'

const formatInr = (n) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(n) || 0)

function feePaid(student) {
  if (typeof student?.totalFeePaid === 'number') return student.totalFeePaid
  if (!Array.isArray(student?.feePayments)) return 0
  return student.feePayments.reduce((s, p) => s + (Number(p.amount) || 0), 0)
}

function feeDue(student) {
  if (typeof student?.feeBalanceDue === 'number') return student.feeBalanceDue
  const cf = Number(student?.courseFee) || 0
  return Math.max(0, cf - feePaid(student))
}

const Student = () => {
  const [searchParams, setSearchParams] = useState({
    enrollmentNo: '',
    name: '',
    semester: '',
    branch: '',
    aparId: '',
  })
  const [students, setStudents] = useState([])
  const [branches, setBranches] = useState([])
  const [dataLoading, setDataLoading] = useState(false)
  const [showAddForm, setShowAddForm] = useState(false)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const [selectedStudentId, setSelectedStudentId] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [file, setFile] = useState(null)
  const [hasSearched, setHasSearched] = useState(false)
  const [feeEmiModalStudent, setFeeEmiModalStudent] = useState(null)
  const [feeEmiAmount, setFeeEmiAmount] = useState('')
  const [feeEmiNote, setFeeEmiNote] = useState('')
  const userToken = localStorage.getItem('userToken')

  const [mpTracks, setMpTracks] = useState([])

  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    aparId: '',
    methodPaperSubSubject: '',
    phone: '',
    semester: '',
    branchId: '',
    gender: '',
    dob: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    country: '',
    profile: '',
    status: 'active',
    bloodGroup: '',
    emergencyContact: {
      name: '',
      relationship: '',
      phone: '',
    },
    courseFee: '',
    initialPayment: '',
  })

  useEffect(() => {
    getBranchHandler()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!showAddForm || !formData.branchId || !formData.semester) {
      setMpTracks([])
      return
    }
    const loadTracks = async () => {
      try {
        const res = await axiosWrapper.get(
          `/method-paper-sub-subjects?branch=${formData.branchId}&semester=${formData.semester}&active=true`,
          { headers: { Authorization: `Bearer ${userToken}` } }
        )
        if (res.data.success) setMpTracks(res.data.data || [])
        else setMpTracks([])
      } catch {
        setMpTracks([])
      }
    }
    loadTracks()
  }, [showAddForm, formData.branchId, formData.semester, userToken])

  const getBranchHandler = async () => {
    try {
      toast.loading('Loading branches...')
      const response = await axiosWrapper.get(`/branch`, {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      })
      if (response.data.success) {
        setBranches(response.data.data)
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setBranches([])
      } else {
        console.error(error)
        toast.error(error.response?.data?.message || 'Error fetching branches')
      }
    } finally {
      toast.dismiss()
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setSearchParams((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const searchStudents = async (e) => {
    e.preventDefault()

    if (
      !searchParams.enrollmentNo &&
      !searchParams.name &&
      !searchParams.semester &&
      !searchParams.branch &&
      !String(searchParams.aparId || '').trim()
    ) {
      toast.error('Please select at least one filter')
      return
    }

    setDataLoading(true)
    setHasSearched(true)
    toast.loading('Searching students...')
    try {
      const response = await axiosWrapper.post(
        `/student/search`,
        searchParams,
        {
          headers: { Authorization: `Bearer ${userToken}` },
        }
      )

      toast.dismiss()
      if (response.data.success) {
        if (response.data.data.length === 0) {
          setStudents([])
        } else {
          toast.success('Students found!')
          setStudents(response.data.data)
        }
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.dismiss()
      setStudents([])
      toast.error(error.response?.data?.message || 'Error searching students')
    } finally {
      setDataLoading(false)
    }
  }

  const runSearchSameFilters = async () => {
    if (
      !searchParams.enrollmentNo &&
      !searchParams.name &&
      !searchParams.semester &&
      !searchParams.branch &&
      !String(searchParams.aparId || '').trim()
    ) {
      return
    }
    setDataLoading(true)
    try {
      const response = await axiosWrapper.post(
        `/student/search`,
        searchParams,
        { headers: { Authorization: `Bearer ${userToken}` } }
      )
      if (response.data.success && Array.isArray(response.data.data)) {
        setStudents(response.data.data)
      }
    } catch {
      /* ignore */
    } finally {
      setDataLoading(false)
    }
  }

  const handleFormInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleEmergencyContactChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      emergencyContact: {
        ...prev.emergencyContact,
        [field]: value,
      },
    }))
  }

  const addStudentHandler = async () => {
    try {
      toast.loading(isEditing ? 'Updating Student' : 'Adding Student')
      const headers = {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${userToken}`,
      }

      const formDataToSend = new FormData()
      for (const key in formData) {
        if (key === 'emergencyContact') {
          for (const subKey in formData.emergencyContact) {
            formDataToSend.append(
              `emergencyContact[${subKey}]`,
              formData.emergencyContact[subKey]
            )
          }
        } else if (key === 'initialPayment' && isEditing) {
          continue
        } else if (key === 'courseFee') {
          const v = formData[key]
          formDataToSend.append(
            key,
            v === '' || v == null ? '0' : String(v)
          )
        } else if (key === 'initialPayment') {
          const v = formData[key]
          formDataToSend.append(key, v === '' || v == null ? '0' : String(v))
        } else {
          formDataToSend.append(key, formData[key] ?? '')
        }
      }

      if (file) {
        formDataToSend.append('file', file)
      }

      let response
      if (isEditing) {
        response = await axiosWrapper.patch(
          `/student/${selectedStudentId}`,
          formDataToSend,
          {
            headers,
          }
        )
      } else {
        response = await axiosWrapper.post(
          `/student/register`,
          formDataToSend,
          {
            headers,
          }
        )
      }

      toast.dismiss()
      if (response.data.success) {
        if (!isEditing) {
          toast.success(
            `Student created successfully! Default password: student123`
          )
        } else {
          toast.success(response.data.message)
        }
        resetForm()
        if (hasSearched) {
          runSearchSameFilters()
        }
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.dismiss()
      toast.error(error.response?.data?.message || 'Error')
    }
  }

  const submitFeePayment = async () => {
    const amt = Number(feeEmiAmount)
    if (!feeEmiModalStudent || !Number.isFinite(amt) || amt <= 0) {
      toast.error('Enter a valid amount greater than 0')
      return
    }
    try {
      toast.loading('Recording payment...')
      const res = await axiosWrapper.post(
        `/student/${feeEmiModalStudent._id}/fee-payment`,
        { amount: amt, note: feeEmiNote.trim() },
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
            'Content-Type': 'application/json',
          },
        }
      )
      toast.dismiss()
      if (res.data.success) {
        toast.success(res.data.message || 'Payment recorded')
        const updated = res.data.data
        setStudents((prev) =>
          prev.map((s) => (s._id === updated._id ? { ...s, ...updated } : s))
        )
        setFeeEmiModalStudent(null)
        setFeeEmiAmount('')
        setFeeEmiNote('')
      } else {
        toast.error(res.data.message || 'Failed')
      }
    } catch (error) {
      toast.dismiss()
      toast.error(error.response?.data?.message || 'Error recording payment')
    }
  }

  const deleteStudentHandler = (id) => {
    setIsDeleteConfirmOpen(true)
    setSelectedStudentId(id)
  }

  const editStudentHandler = (student) => {
    setFormData({
      firstName: student.firstName || '',
      middleName: student.middleName || '',
      lastName: student.lastName || '',
      email: student.email || '',
      aparId: student.aparId || '',
      methodPaperSubSubject: student.methodPaperSubSubject?._id || '',
      phone: student.phone || '',
      semester: student.semester || '',
      branchId: student.branchId?._id || '',
      gender: student.gender || '',
      dob: student.dob?.split('T')[0] || '',
      address: student.address || '',
      city: student.city || '',
      state: student.state || '',
      pincode: student.pincode || '',
      country: student.country || '',
      profile: student.profile || '',
      status: student.status || 'active',
      bloodGroup: student.bloodGroup || '',
      emergencyContact: {
        name: student.emergencyContact?.name || '',
        relationship: student.emergencyContact?.relationship || '',
        phone: student.emergencyContact?.phone || '',
      },
      courseFee:
        student.courseFee != null && student.courseFee !== ''
          ? String(student.courseFee)
          : '',
      initialPayment: '',
    })
    setSelectedStudentId(student._id)
    setIsEditing(true)
    setShowAddForm(true)
  }

  const confirmDelete = async () => {
    try {
      toast.loading('Deleting Student')
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userToken}`,
      }
      const response = await axiosWrapper.delete(
        `/student/${selectedStudentId}`,
        {
          headers,
        }
      )
      toast.dismiss()
      if (response.data.success) {
        toast.success('Student has been deleted successfully')
        setIsDeleteConfirmOpen(false)
        runSearchSameFilters()
      } else {
        toast.error(response.data.message)
      }
    } catch (error) {
      toast.dismiss()
      toast.error(error.response?.data?.message || 'Error')
    }
  }

  const resetForm = (keepOpen = false) => {
    setFormData({
      firstName: '',
      middleName: '',
      lastName: '',
      email: '',
      aparId: '',
      methodPaperSubSubject: '',
      phone: '',
      semester: '',
      branchId: '',
      gender: '',
      dob: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      country: '',
      profile: '',
      status: 'active',
      bloodGroup: '',
      emergencyContact: {
        name: '',
        relationship: '',
        phone: '',
      },
      courseFee: '',
      initialPayment: '',
    })
    setShowAddForm(keepOpen)
    setIsEditing(false)
    setSelectedStudentId(null)
    setFile(null)
  }

  return (
    <div className="w-full mx-auto mt-10 flex justify-center items-start flex-col mb-10">
      <div className="flex justify-between items-center w-full">
        <Heading title="Student Management" />
        {branches.length > 0 && (
          <CustomButton onClick={() => resetForm(true)}>
            <IoMdAdd className="text-2xl" />
          </CustomButton>
        )}
      </div>

      {branches.length > 0 && (
        <div className="my-6 mx-auto w-full">
          <form onSubmit={searchStudents} className="flex items-center">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 w-[90%] mx-auto">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Enrollment Number
                </label>
                <input
                  type="text"
                  name="enrollmentNo"
                  value={searchParams.enrollmentNo}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter enrollment number"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={searchParams.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter student name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Semester
                </label>
                <select
                  name="semester"
                  value={searchParams.semester}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Semester</option>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map((sem) => (
                    <option key={sem} value={sem}>
                      Semester {sem}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Branch
                </label>
                <select
                  name="branch"
                  value={searchParams.branch}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Branch</option>
                  {branches?.map((branch) => (
                    <option key={branch._id} value={branch._id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6 flex justify-center w-[10%] mx-auto">
              <CustomButton
                type="submit"
                disabled={dataLoading}
                variant="primary"
              >
                {dataLoading ? 'Searching...' : 'Search'}
              </CustomButton>
            </div>
          </form>

          {!hasSearched && (
            <div className="text-center mt-8 text-gray-600 flex flex-col items-center justify-center my-10 bg-white p-10 rounded-lg mx-auto w-[40%]">
              <img
                src="/assets/filter.svg"
                alt="Select filters"
                className="w-64 h-64 mb-4"
              />
              Please select at least one filter to search students
            </div>
          )}

          {hasSearched && students.length === 0 && (
            <NoData title="No students found" />
          )}

          {students && students.length > 0 && (
            <div className="mt-8">
              <h2 className="text-xl font-semibold mb-4">Search Results</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-6 py-3 border-b text-left">Profile</th>
                      <th className="px-6 py-3 border-b text-left">Name</th>
                      <th className="px-6 py-3 border-b text-left">E. No</th>
                      <th className="px-6 py-3 border-b text-left">APAR ID</th>
                      <th className="px-6 py-3 border-b text-left">Method paper</th>
                      <th className="px-6 py-3 border-b text-left">Semester</th>
                      <th className="px-6 py-3 border-b text-left">Branch</th>
                      <th className="px-6 py-3 border-b text-right whitespace-nowrap">
                        Course fee
                      </th>
                      <th className="px-6 py-3 border-b text-right whitespace-nowrap">
                        Paid
                      </th>
                      <th className="px-6 py-3 border-b text-right whitespace-nowrap">
                        Remaining
                      </th>
                      <th className="px-6 py-3 border-b text-left">Email</th>
                      <th className="px-6 py-3 border-b text-center">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((student) => (
                      <tr key={student._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 border-b">
                          <img
                            src={`${process.env.REACT_APP_MEDIA_LINK}/${student.profile}`}
                            alt={`${student.firstName}'s profile`}
                            className="w-12 h-12 object-cover rounded-full"
                            onError={(e) => {
                              e.target.src =
                                'https://images.unsplash.com/photo-1744315900478-fa44dc6a4e89?q=80&w=3087&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D'
                            }}
                          />
                        </td>
                        <td className="px-6 py-4 border-b">
                          {student.firstName} {student.middleName}{' '}
                          {student.lastName}
                        </td>
                        <td className="px-6 py-4 border-b">
                          {student.enrollmentNo}
                        </td>
                        <td className="px-6 py-4 border-b">
                          {student.aparId || '—'}
                        </td>
                        <td className="px-6 py-4 border-b text-sm">
                          {student.methodPaperSubSubject?.name || '—'}
                        </td>
                        <td className="px-6 py-4 border-b">
                          {student.semester}
                        </td>
                        <td className="px-6 py-4 border-b">
                          {student.branchId?.name}
                        </td>
                        <td className="px-6 py-4 border-b text-right text-sm tabular-nums">
                          {formatInr(student.courseFee)}
                        </td>
                        <td className="px-6 py-4 border-b text-right text-sm tabular-nums text-emerald-700">
                          {formatInr(feePaid(student))}
                        </td>
                        <td className="px-6 py-4 border-b text-right text-sm tabular-nums font-semibold text-amber-800">
                          {formatInr(feeDue(student))}
                        </td>
                        <td className="px-6 py-4 border-b">{student.email}</td>
                        <td className="px-6 py-4 border-b text-center">
                          <div className="flex justify-center flex-wrap gap-2">
                            <CustomButton
                              variant="secondary"
                              className="!px-2 !py-1 text-xs"
                              title="Record EMI / fee instalment"
                              onClick={() => {
                                setFeeEmiModalStudent(student)
                                setFeeEmiAmount('')
                                setFeeEmiNote('')
                              }}
                            >
                              + EMI
                            </CustomButton>
                            <CustomButton
                              variant="secondary"
                              className="!p-2"
                              onClick={() => editStudentHandler(student)}
                            >
                              <MdEdit />
                            </CustomButton>
                            <CustomButton
                              variant="danger"
                              className="!p-2"
                              onClick={() => deleteStudentHandler(student._id)}
                            >
                              <MdOutlineDelete />
                            </CustomButton>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {branches.length === 0 && (
        <div className="flex justify-center items-center flex-col w-full mt-24">
          <CgDanger className="w-16 h-16 text-yellow-500 mb-4" />
          <p className="text-center text-lg">
            Please add branches before adding a student.
          </p>
        </div>
      )}

      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 w-[90%] max-w-4xl max-h-[90vh] overflow-y-auto relative">
            <button
              type="button"
              onClick={() => resetForm()}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100 transition-colors"
            >
              <IoMdClose className="text-2xl" />
            </button>
            <h2 className="text-2xl font-semibold mb-6">
              {isEditing ? 'Edit Student' : 'Add New Student'}
            </h2>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                addStudentHandler()
              }}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) =>
                      handleFormInputChange('firstName', e.target.value)
                    }
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Middle Name
                  </label>
                  <input
                    type="text"
                    value={formData.middleName}
                    onChange={(e) =>
                      handleFormInputChange('middleName', e.target.value)
                    }
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) =>
                      handleFormInputChange('lastName', e.target.value)
                    }
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="text"
                    value={formData.email}
                    onChange={(e) =>
                      handleFormInputChange('email', e.target.value)
                    }
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) =>
                      handleFormInputChange('phone', e.target.value)
                    }
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Semester
                  </label>
                  <select
                    value={formData.semester}
                    onChange={(e) =>
                      handleFormInputChange('semester', e.target.value)
                    }
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Semester</option>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((sem) => (
                      <option key={sem} value={sem}>
                        Semester {sem}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Branch
                  </label>
                  <select
                    value={formData.branchId}
                    onChange={(e) =>
                      handleFormInputChange('branchId', e.target.value)
                    }
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Branch</option>
                    {branches?.map((branch) => (
                      <option key={branch._id} value={branch._id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    APAR ID
                  </label>
                  <input
                    type="text"
                    value={formData.aparId}
                    onChange={(e) =>
                      handleFormInputChange('aparId', e.target.value)
                    }
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Optional — must be unique if set"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Method paper sub-subject
                  </label>
                  <select
                    value={formData.methodPaperSubSubject}
                    onChange={(e) =>
                      handleFormInputChange(
                        'methodPaperSubSubject',
                        e.target.value
                      )
                    }
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={!formData.branchId || !formData.semester}
                  >
                    <option value="">
                      {!formData.branchId || !formData.semester
                        ? 'Select branch & semester first'
                        : 'None / not chosen'}
                    </option>
                    {mpTracks.map((t) => (
                      <option key={t._id} value={t._id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Options come from Admin → MP Sub-subjects for this branch &
                    semester.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Course fee (₹)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={formData.courseFee}
                    onChange={(e) =>
                      handleFormInputChange('courseFee', e.target.value)
                    }
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g. 60000"
                  />
                </div>

                {!isEditing && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Initial payment (₹)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={formData.initialPayment}
                      onChange={(e) =>
                        handleFormInputChange('initialPayment', e.target.value)
                      }
                      className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Amount received now (optional)"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      First instalment at admission. More payments: use + EMI in
                      the student list after search.
                    </p>
                  </div>
                )}

                {isEditing && (
                  <div className="md:col-span-2 rounded-lg border border-blue-100 bg-blue-50/60 px-4 py-3 text-sm text-gray-700">
                    <strong>Fee:</strong> course fee and payments are updated here
                    (course fee field above) or via <strong>+ EMI</strong> in the
                    search results table — instalments add to total paid and
                    reduce remaining automatically.
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Gender
                  </label>
                  <select
                    value={formData.gender}
                    onChange={(e) =>
                      handleFormInputChange('gender', e.target.value)
                    }
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    value={formData.dob}
                    onChange={(e) =>
                      handleFormInputChange('dob', e.target.value)
                    }
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Blood Group
                  </label>
                  <select
                    value={formData.bloodGroup}
                    onChange={(e) =>
                      handleFormInputChange('bloodGroup', e.target.value)
                    }
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Blood Group</option>
                    <option value="A+">A+</option>
                    <option value="A-">A-</option>
                    <option value="B+">B+</option>
                    <option value="B-">B-</option>
                    <option value="AB+">AB+</option>
                    <option value="AB-">AB-</option>
                    <option value="O+">O+</option>
                    <option value="O-">O-</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Profile Photo
                  </label>
                  <input
                    type="file"
                    onChange={(e) => setFile(e.target.files[0])}
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    accept="image/*"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) =>
                      handleFormInputChange('address', e.target.value)
                    }
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) =>
                      handleFormInputChange('city', e.target.value)
                    }
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) =>
                      handleFormInputChange('state', e.target.value)
                    }
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pincode
                  </label>
                  <input
                    type="text"
                    value={formData.pincode}
                    onChange={(e) =>
                      handleFormInputChange('pincode', e.target.value)
                    }
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) =>
                      handleFormInputChange('country', e.target.value)
                    }
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <h3 className="text-lg font-semibold mb-4">
                    Emergency Contact
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        value={formData.emergencyContact.name}
                        onChange={(e) =>
                          handleEmergencyContactChange('name', e.target.value)
                        }
                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Relationship
                      </label>
                      <input
                        type="text"
                        value={formData.emergencyContact.relationship}
                        onChange={(e) =>
                          handleEmergencyContactChange(
                            'relationship',
                            e.target.value
                          )
                        }
                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={formData.emergencyContact.phone}
                        onChange={(e) =>
                          handleEmergencyContactChange('phone', e.target.value)
                        }
                        className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-between items-center gap-4">
                <div>
                  <p className="text-sm">
                    Default login will be{' '}
                    <span className="font-bold">
                      {formData.enrollmentNo || 'enrollment_no'}@gmail.com
                    </span>{' '}
                    and password will be{' '}
                    <span className="font-bold">student123</span>
                  </p>
                </div>
                <div className="flex gap-4">
                  <CustomButton
                    type="button"
                    variant="secondary"
                    onClick={() => resetForm()}
                  >
                    Cancel
                  </CustomButton>
                  <CustomButton type="submit" variant="primary">
                    {isEditing ? 'Update Student' : 'Add Student'}
                  </CustomButton>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {feeEmiModalStudent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 relative">
            <button
              type="button"
              onClick={() => {
                setFeeEmiModalStudent(null)
                setFeeEmiAmount('')
                setFeeEmiNote('')
              }}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-800"
              aria-label="Close"
            >
              <IoMdClose className="text-2xl" />
            </button>
            <h3 className="text-lg font-bold text-gray-900 pr-8">
              Record fee instalment
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {feeEmiModalStudent.firstName} {feeEmiModalStudent.lastName} · Enr.{' '}
              {feeEmiModalStudent.enrollmentNo}
            </p>
            <p className="text-xs text-gray-500 mt-2 space-x-3">
              <span>Course fee: {formatInr(feeEmiModalStudent.courseFee)}</span>
              <span>Paid: {formatInr(feePaid(feeEmiModalStudent))}</span>
              <span className="font-semibold text-amber-800">
                Due: {formatInr(feeDue(feeEmiModalStudent))}
              </span>
            </p>
            <div className="mt-4 space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Amount received (₹)
                <input
                  type="number"
                  min="1"
                  step="1"
                  className="mt-1 w-full px-3 py-2 border rounded-lg"
                  value={feeEmiAmount}
                  onChange={(e) => setFeeEmiAmount(e.target.value)}
                  placeholder="e.g. 10000"
                  autoFocus
                />
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Note (optional)
                <input
                  type="text"
                  className="mt-1 w-full px-3 py-2 border rounded-lg"
                  value={feeEmiNote}
                  onChange={(e) => setFeeEmiNote(e.target.value)}
                  placeholder="e.g. April EMI"
                />
              </label>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <CustomButton
                type="button"
                variant="secondary"
                onClick={() => {
                  setFeeEmiModalStudent(null)
                  setFeeEmiAmount('')
                  setFeeEmiNote('')
                }}
              >
                Cancel
              </CustomButton>
              <CustomButton type="button" variant="primary" onClick={submitFeePayment}>
                Save payment
              </CustomButton>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirm
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={confirmDelete}
        message="Are you sure you want to delete this student?"
      />
    </div>
  )
}

export default Student
